import { useOutletContext } from "react-router-dom";
import useStyles from "./styles";
import { useTheme } from "@emotion/react";
import {
  Autocomplete,
  Grid,
  LinearProgress,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import StatusComponent from "../../../../components/statusComponent";
import CardDataComponent from "../../../../components/overview/cardDataComponent";
import { Box, flexbox, Stack } from "@mui/system";
import DataGraphComponent from "../../../../components/overview/dataGraphComponent";
import CardStatusComponent from "../../../../components/overview/cardStatusComponent";
import LoaderThreeDots from "../../../../components/loadingAnimation";

import { useEffect, useState } from "react";

import dayjs from "dayjs";
import CardGraphComponent from "../../../../components/cardGraphComponent";
import {
  DownloadOutlined,
  NetworkPing,
  PublishOutlined,
  SpeedOutlined,
  TrendingDownOutlined,
  SettingsEthernetOutlined,
} from "@mui/icons-material";
import LineChartComponent from "../../../../components/lineComponent";
import AlertsTable from "./alertsTable";
import PingDetailModal from "./pingDetailModal";
import ChartTooltip from "./chartTooltip";
import {
  INVERSED_POWER_RX,
  formatTraffic,
  formatPing,
  formatSessions,
  formatPowerValue,
  formatPacketLoss,
} from "./formatters";
import { createOverviewFetchers } from "./overviewFetchers";

const OVERVIEW_METRICS = [
  "trafficIn",
  "trafficOut",
  "erroPacketIn",
  "erroPacketOut",
  "ifSpeed",
  "packetLoss",
  "latency",
  "statusInterface",
  "sessoesAtivas",
];

export default function DashOverview() {
  //#region Hooks
  const theme = useTheme();
  const styles = useStyles(theme);

  const {
    filterData,
    handleAplicarZoom,
    setCounter,
    setLoadingCronometro,
    selectTime,
    optionsTime,
    startCronometro,
    controllerDashboard,
    setControllerDashboard,
    toggleDrawer,
    api,
    stopCronometro,
    deviceConfig,
  } = useOutletContext();
  //#endregion

  //#region useStates
  const [availableMetrics, setAvailableMetrics] = useState([]);
  const [hostRef, setHostRef] = useState(null);

  const [errorPacketCount, setErrorPacketCount] = useState({
    in: 0,
    out: 0,
  });
  const [trafficNow, setTrafficNow] = useState({
    in: "0 KB/s",
    out: "0 KB/s",
  });

  const [loadingDashOverview, setLoadingDashOverview] = useState({
    trafficIn: false,
    trafficOut: false,
    statusInterface: false,
    erroPacketIn: false,
    erroPacketOut: false,
    speed: false,
    ping: false,
    packetLoss: false,
    latency: false,
    alerts: false,
    threeDots: false,
    sessoesAtivas: false,
  });


  const [trafficInput, setTrafficInput] = useState([]);
  const [trafficOutput, setTrafficOutput] = useState([]);
  const [statusInterface, setStatusInterface] = useState(true);
  const [speedNow, setSpeedNow] = useState("0 KB/s");
  const [rows, setRows] = useState([]);
  const [pingDestinos, setPingDestinos] = useState([]);
  const [pingHabilitado, setPingHabilitado] = useState(null);
  const [packetLoss, setPacketLoss] = useState([]);

  const [latency, setLatency] = useState([]);
  const [sessoesAtivas, setSessoesAtivas] = useState([]);

  // estados do modal de histórico de ping por destino
  const [pingModalOpen, setPingModalOpen] = useState(false);
  const [pingModalDestino, setPingModalDestino] = useState(null);
  const [pingHistoryData, setPingHistoryData] = useState({
    latency: [],
    packetLoss: [],
  });
  const [pingHistoryLoading, setPingHistoryLoading] = useState({
    latency: false,
    packetLoss: false,
  });
  // indica se o usuário fez zoom manual — congela a sincronização com o pai
  const [pingModalZoomed, setPingModalZoomed] = useState(false);

  /**
   * Verifica se o serviço selecionado possui ONU associada
   * se existir, exibe as potências
   */
  const [onu, setOnu] = useState({});
  const [onuPower, setOnuPower] = useState({});
  const [oltHosts, setOltHosts] = useState([]);
  const [selectedOnu, setSelectedOnu] = useState(null);
  const [loadingPower, setLoadingPower] = useState({
    rx: false,
    tx: false,
  });
  //#endregion

  //#region useEffects
  useEffect(() => {
    if (
      filterData?.interfaceId &&
      filterData?.hostId &&
      filterData?.initialDate &&
      filterData?.finalDate
    ) {
      toggleDrawer(false)();
      if (filterData?.hostId !== hostRef) {
        setHostRef(filterData?.hostId);
        setAvailableMetrics([]);
        discoverMetrics();
      } else {
        fetchAllData();
      }
    } else {
      clearData();
    }
  }, [filterData]);

  useEffect(() => {
    if (
      filterData?.interfaceId &&
      filterData?.hostId &&
      filterData?.initialDate &&
      filterData?.finalDate &&
      availableMetrics?.length > 0
    ) {
      fetchAllData();
    }
  }, [availableMetrics]);

  useEffect(() => {
    return () => {
      if (controllerDashboard) {
        controllerDashboard.abort();
      }
      stopCronometro();
      setPingDestinos((prevDestinos) => {
        prevDestinos.forEach((destino) => {
          if (
            destino?.image &&
            typeof destino?.image === "string" &&
            destino?.image.startsWith("blob:")
          ) {
            URL.revokeObjectURL(destino?.image);
          }
        });
        return prevDestinos;
      });
    };
  }, []);

  useEffect(() => {
    if (selectedOnu) {
      // Limpa os dados de power quando a ONU selecionada muda
      setOnuPower({ rx: [], tx: [] });

      // Obtem o range da consulta
      let dateInitial = filterData?.initialDate;
      let finalDate = filterData?.finalDate;

      if (selectTime || selectTime === 0) {
        dateInitial = optionsTime[selectTime]?.value;
        finalDate = dayjs().set("second", 0).set("millisecond", 0).toDate();
      }

      // Usa o signal do controllerDashboard existente
      const signal = controllerDashboard?.signal;

      // Busca os dados de power para a ONU selecionada
      getOnuByService(signal, dateInitial, finalDate);
    }
  }, [selectedOnu]);
  //#endregion

  //#region APIs
  const {
    OVERVIEW_FETCHERS,
    runOverviewFetch,
    getStatusInterface,
    getPing,
    getPingHistory,
    getAlerts,
    getOnuByService,
  } = createOverviewFetchers({
    api,
    filterData,
    selectedOnu,
    oltHosts,
    onu,
    loadingPower,
    setLoadingDashOverview,
    setTrafficInput,
    setTrafficOutput,
    setTrafficNow,
    setPacketLoss,
    setLatency,
    setSessoesAtivas,
    setErrorPacketCount,
    setSpeedNow,
    setStatusInterface,
    setRows,
    setPingDestinos,
    setPingHabilitado,
    setPingHistoryData,
    setPingHistoryLoading,
    setOnu,
    setSelectedOnu,
    setOltHosts,
    setOnuPower,
  });
  //#endregion

  //#region Funções
  // abre o modal e busca os dados iniciais do destino selecionado
  const handlePingCardClick = (destino) => {
    setPingModalDestino(destino);
    setPingModalOpen(true);
    setPingHistoryData({ latency: [], packetLoss: [] });

    // calcula o range da mesma forma que fetchAllData
    let dateInitial = filterData?.initialDate;
    let finalDate = filterData?.finalDate;
    if (selectTime || selectTime === 0) {
      dateInitial = optionsTime?.[selectTime]?.value;
      finalDate = dayjs().set("second", 0).set("millisecond", 0).toDate();
    }

    getPingHistory(
      destino,
      controllerDashboard?.signal,
      dateInitial,
      finalDate,
    );
  };

  // zoom interno do modal: fixa o range e para de acompanhar o pai
  const handlePingModalZoom = (startDate, endDate) => {
    if (!pingModalDestino) return;
    setPingModalZoomed(true);
    // controller próprio para não ser cancelado pelo ciclo do pai
    const modalController = new AbortController();
    getPingHistory(
      pingModalDestino,
      modalController?.signal,
      dayjs(startDate)?.toDate(),
      dayjs(endDate)?.toDate(),
    );
  };

  // fecha o modal e limpa os dados de histórico
  const handlePingModalClose = () => {
    setPingModalOpen(false);
    setPingModalDestino(null);
    setPingHistoryData({ latency: [], packetLoss: [] });
    setPingHistoryLoading({ latency: false, packetLoss: false });
    setPingModalZoomed(false);
  };

  const fetchAllData = async () => {
    if (availableMetrics?.length === 0) return;

    setCounter(null);
    stopCronometro();
    setLoadingCronometro(true);

    let dateInitial = filterData?.initialDate;
    let finalDate = filterData?.finalDate;

    if (selectTime || selectTime === 0) {
      dateInitial = optionsTime[selectTime]?.value;
      finalDate = dayjs().set("second", 0).set("millisecond", 0).toDate();
    }
    if (controllerDashboard) {
      controllerDashboard.abort();
    }
    const newController = new AbortController();
    const signal = newController.signal;
    setControllerDashboard(newController);

    const promises = [
      ...OVERVIEW_FETCHERS.map((fetcher) =>
        runOverviewFetch(fetcher, dateInitial, finalDate, signal),
      ),
      getStatusInterface(dateInitial, finalDate, signal),
      getAlerts(dateInitial, finalDate, signal),
      getPing(dateInitial, finalDate, signal),
      getOnuByService(signal, dateInitial, finalDate),
    ];

    // herda o ciclo de refresh do pai quando o modal está aberto e sem zoom fixado
    if (pingModalOpen && !pingModalZoomed && pingModalDestino) {
      promises.push(
        getPingHistory(pingModalDestino, signal, dateInitial, finalDate),
      );
    }

    const responsePromises = await Promise.all(promises);

    if (!signal.aborted) {
      setLoadingCronometro(false);
      startCronometro();
    }
  };

  const discoverMetrics = async () => {
    try {

      if (!filterData?.hostId) return;

      setLoadingDashOverview((prev) => ({ ...prev, threeDots: true }));

      const newController = new AbortController();
      const signal = newController.signal;

      // Sem `dash`: o backend une as métricas de todas as seções do schema do
      // host, mantendo o superconjunto que o antigo lookup por device retornava
      const response = await api.post(
        "/zabbix/items/list",
        {
          hostid: filterData?.hostId,
          filterBySchema: true,
          fabricante: filterData?.fabricante,
          funcao: filterData?.funcao,
          modelo: filterData?.modelo,
        },
        { signal },
      );

      // se o backend retornar erro (não-array), usa todas as métricas como fallback

      const items = response?.data?.items;
      const available = Array.isArray(items)
        ? OVERVIEW_METRICS.filter((metric) => items.includes(metric))
        : OVERVIEW_METRICS;

      setAvailableMetrics(available);
    } catch (error) {
      console.error("Erro ao buscar lista de métricas:", error);
      setAvailableMetrics(OVERVIEW_METRICS); // Fallback para todas as métricas
    } finally {
      setLoadingDashOverview((prev) => ({ ...prev, threeDots: false }));
    }
  };

  const clearData = () => {
    setErrorPacketCount({ in: 0, out: 0 });
    setTrafficNow({ in: "0 KB/s", out: "0 KB/s" });
    setTrafficInput([]);
    setTrafficOutput([]);
    setStatusInterface(true);
    setSpeedNow("0 KB/s");
    setRows([]);
    setPingDestinos((prevDestinos) => {
      prevDestinos.forEach((destino) => {
        if (
          destino?.image &&
          typeof destino?.image === "string" &&
          destino?.image.startsWith("blob:")
        ) {
          URL.revokeObjectURL(destino?.image);
        }
      });
      return [];
    });
    setPingHabilitado(null);
    setPacketLoss([]);
    setLatency([]);
    setSessoesAtivas([]);
    setOnu({});
    setOnuPower({});
    setOltHosts([]);
    setSelectedOnu(null);
    // limpa o modal de histórico de ping ao trocar dispositivo/filtros
    setPingModalOpen(false);
    setPingModalDestino(null);
    setPingHistoryData({ latency: [], packetLoss: [] });
    setPingHistoryLoading({ latency: false, packetLoss: false });
    setPingModalZoomed(false);
  };
  //#endregion

  //#region Componentes

  const CustomTooltipPower = ({ active, payload, label, rx = false }) => {
    if (!active || !payload || payload.length === 0) return null;

    const { clock, value } = payload[0].payload;
    const toFixedValue = value?.toFixed(2);

    return (
      <Box
        sx={{
          bgcolor: theme.palette.paletteCharts.lineCharts,
          padding: "8px",
          borderRadius: "8px",
        }}
      >
        <Typography fontSize={"14px"}>
          {`Data: ${dayjs.unix(clock).format("DD/MM/YY HH:mm")}`}
        </Typography>

        <Stack direction={"row"} alignItems={"center"} gap={"4px"}>
          <Box
            sx={{
              bgcolor:
                theme.palette.cardGraphic.graphColor[
                value > 80
                  ? "red"
                  : value <= 80 && value > 40
                    ? "yellow"
                    : "green"
                ],
              width: "14px",
              height: "14px",
              borderRadius: "2px",
              border: `1px solid ${theme.palette.cardGraphic.graphColor[
                value > 80
                  ? "red"
                  : value <= 80 && value > 40
                    ? "yellow"
                    : "green"
              ]
                }`,
            }}
          />
          <Typography fontSize={"14px"}>
            Potência: {rx ? -toFixedValue : toFixedValue}
          </Typography>
        </Stack>
      </Box>
    );
  };

  const AutocompleteOnu = (
    <Autocomplete
      options={onu?.onu || []}
      value={selectedOnu}
      onChange={(event, newValue) => {
        setSelectedOnu(newValue);
      }}
      getOptionLabel={(option) => option?.onuItem || ""}
      sx={{
        width: 200,
      }}
      renderInput={(params) => (
        <Tooltip title={selectedOnu?.onuItem || ""}>
          <TextField
            {...params}
            label="Selecionar ONU"
            variant="outlined"
            size="small"
            sx={{
              fontSize: "0.75rem",
              "& .MuiInputBase-input": {
                padding: "6px 8px",
                fontSize: "0.75rem",
              },
              "& .MuiInputLabel-root": {
                fontSize: "0.75rem",
              },
            }}
          />
        </Tooltip>
      )}
      disabled={!onu?.onu || onu?.onu?.length === 0}
    />
  );

  //#endregion

  return (
    <>
      <Box sx={styles.container}>
        <Grid
          container
          spacing={1}
          columns={{ xs: 2, sm: 4, md: 6, lg: 6, xl: 6, xxl: 6 }}
        >
          <Grid item xs={2} sm={4} md={6} lg={6}>
            <Grid
              container
              columns={{ xs: 2, sm: 4, md: 12, lg: 3, xl: 6, xxl: 6 }}
              spacing={1}
              alignItems="stretch"
            >
              <Grid item xs={2} sm={2} md={4} lg={1}>
                <StatusComponent
                  loading={loadingDashOverview?.statusInterface}
                  status={statusInterface}
                />
              </Grid>
              <Grid item xs={2} sm={2} md={4} lg={1}>
                <CardDataComponent
                  cardTile={"Tráfego (entrada)"}
                  cardValue={trafficNow?.in}
                  cardTextColor={"chip?.main.waitingReview"}
                  typeIcon={2}
                  colorCard="inputData"
                  loading={loadingDashOverview?.trafficIn}
                />
              </Grid>
              <Grid item xs={2} sm={2} md={4} lg={1}>
                <CardDataComponent
                  cardTile={"Tráfego (saída)"}
                  cardValue={trafficNow?.out}
                  cardTextColor={"chip?.main.inProgress"}
                  colorCard="outputData"
                  typeIcon={2}
                  loading={loadingDashOverview?.trafficOut}
                />
              </Grid>
              <Grid item xs={2} sm={2} md={4} lg={1}>
                <CardDataComponent
                  cardTile={"Pacotes com erros (entrada)"}
                  cardValue={errorPacketCount?.in}
                  cardTextColor={"chip?.main.waitingReview"}
                  colorCard="inputData"
                  loading={loadingDashOverview?.erroPacketIn}
                />
              </Grid>

              <Grid item xs={2} sm={2} md={4} lg={1}>
                <CardDataComponent
                  cardTile={"Pacotes com erros (saída)"}
                  cardValue={errorPacketCount?.out}
                  cardTextColor={"chip?.main.inProgress"}
                  colorCard="outputData"
                  loading={loadingDashOverview?.erroPacketOut}
                />
              </Grid>
              <Grid item xs={2} sm={2} md={4} lg={1}>
                <CardDataComponent
                  cardTile={"Velocidade"}
                  cardValue={speedNow}
                  cardTextColor={"chip?.main.waitingReturn"}
                  typeIcon={1}
                  colorCard="speeData"
                  loading={loadingDashOverview?.speed}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Gráfico de sessões ativas — exclusivo para dispositivos Fortigate */}
          {availableMetrics?.includes("sessoesAtivas") && (
            <Grid item xs={2} sm={4} md={6} lg={6} xl={6}>
              <Paper
                sx={{
                  ...styles.paper,
                  backgroundColor: theme.palette.background.paper,
                  position: "relative",
                  overflow: "hidden",
                }}
                elevation={theme.palette.mode === "dark" ? 0 : 3}
              >
                {loadingDashOverview?.sessoesAtivas && (
                  <LinearProgress sx={styles.linearProgress} />
                )}
                <Box sx={{ ...styles.containerComponent, width: "100%" }}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "30px",
                        height: "30px",
                        borderRadius: "5px",
                        backgroundColor:
                          theme?.palette?.cardGraphic?.graphColor?.green,
                      }}
                    >
                      <SettingsEthernetOutlined
                        sx={{
                          color: theme.palette.white.main,
                          fontSize: "20px",
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        ...styles.cardSubText,
                        color: theme?.palette?.cardGraphic?.graphColor?.green,
                      }}
                    >
                      Sessões Ativas
                    </Typography>
                  </Box>
                  <Box sx={{ width: "100%", height: "160px" }}>
                    <LineChartComponent
                      data={Array.isArray(sessoesAtivas) ? sessoesAtivas : []}
                      mainColor={theme?.palette?.cardGraphic?.graphColor?.green}
                      mainLineColor={
                        theme?.palette?.cardGraphic?.graphLineColor?.green
                      }
                      objectColorsLine={
                        sessoesAtivas?.[0]?.avg !== undefined
                          ? {
                            avg: theme?.palette?.cardGraphic?.graphColor
                              ?.green,
                            min: theme?.palette?.cardGraphic?.graphColor
                              ?.blue,
                            max: theme?.palette?.cardGraphic?.graphColor
                              ?.orange,
                          }
                          : false
                      }
                      formatValue={formatSessions}
                      isZoomEnabled={true}
                      onZoomChange={(range) => {
                        const startDate = dayjs
                          .unix(range?.start)
                          .tz("America/Sao_Paulo");
                        const endDate = dayjs
                          .unix(range?.end)
                          .tz("America/Sao_Paulo");
                        handleAplicarZoom(startDate, endDate);
                      }}
                      yOptions={{ tickFormatter: formatSessions }}
                      xOptions={{
                        tickFormatter: (clock) =>
                          dayjs
                            .unix(clock)
                            .tz("America/Sao_Paulo")
                            .format("DD/MM HH:mm"),
                      }}
                    />
                  </Box>
                </Box>
              </Paper>
            </Grid>
          )}

          <Grid
            item
            columns={{ xs: 2, sm: 4, md: 6, lg: 6, xl: 6, xxl: 6 }}
            flexGrow={1}
          >
            <Grid
              container
              columns={{ xs: 2, sm: 4, md: 6, lg: 6, xl: 6, xxl: 6 }}
              flexGrow={1}
              spacing={1}
            >
              <Grid item xs={2} sm={4} md={6} lg={6} xl={3}>
                <DataGraphComponent
                  data={Array.isArray(trafficInput) ? trafficInput : []}
                  titleGraph={"Tráfego de entrada"}
                  colorType={2}
                  colorCardComponent={"blue"}
                  formatTraffic={formatTraffic}
                  loading={loadingDashOverview?.trafficIn}
                  handleAplicarZoom={handleAplicarZoom}
                  isZoomEnabled={true}
                />
              </Grid>

              <Grid item xs={2} sm={4} md={6} lg={6} xl={3}>
                <DataGraphComponent
                  data={Array.isArray(trafficOutput) ? trafficOutput : []}
                  titleGraph={"Tráfego de saída"}
                  colorType={4}
                  colorCardComponent={"orange"}
                  formatTraffic={formatTraffic}
                  loading={loadingDashOverview?.trafficOut}
                  handleAplicarZoom={handleAplicarZoom}
                  isZoomEnabled={true}
                />
              </Grid>
            </Grid>
          </Grid>
          {((loadingDashOverview?.ping ? pingHabilitado !== false : pingHabilitado && pingDestinos?.length > 0)) && (
            <Grid item xs={2} sm={4} md={6} lg={6}>
              <Grid
                container
                spacing={1}
                columns={{
                  xs: 2,
                  sm: 4,
                  md: 6,
                  lg: pingDestinos?.length || 5,
                  xl: pingDestinos?.length || 5,
                  xxl: pingDestinos?.length || 5,
                }}
                flexGrow={1}
                alignItems="stretch"
              >
                {(loadingDashOverview?.ping && pingDestinos?.length === 0
                  ? Array.from({ length: 5 })
                  : pingDestinos
                )?.map((destino, index) => {
                  const isSkeleton = !destino;
                  return (
                    <Grid
                      item
                      xs={2}
                      sm={2}
                      md={2}
                      lg={1}
                      xl={1}
                      key={
                        isSkeleton
                          ? `ping-skeleton-${index}`
                          : destino?.macro || index
                      }
                    >
                      <CardStatusComponent
                        skeleton={isSkeleton}
                        cardName={destino?.description}
                        CardImage={destino?.image || NetworkPing}
                        value={destino?.pingData}
                        loading={loadingDashOverview?.ping}
                        onClick={
                          isSkeleton
                            ? undefined
                            : () => handlePingCardClick(destino)
                        }
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          )}
          <Grid item xs={2} sm={4} md={6} lg={6}>
            <Grid
              container
              columns={{ xs: 2, sm: 4, md: 6, lg: 6, xl: 6, xxl: 6 }}
              flexGrow={1}
              spacing={1}
            >
              <Grid item xs={2} sm={4} md={6} lg={6} xl={3}>
                <CardGraphComponent
                  loading={loadingDashOverview?.packetLoss}
                  titleGraph={"Perda de pacotes"}
                  data={Array.isArray(packetLoss) ? packetLoss : []}
                  icon={TrendingDownOutlined}
                  valueCard={formatPacketLoss(
                    Array.isArray(packetLoss)
                      ? packetLoss[packetLoss?.length - 1]?.value || 0
                      : 0,
                  )}
                  CustomTooltip={
                    <ChartTooltip title="Loss" formatValue={formatPacketLoss} />
                  }
                  tickFormatter={formatPacketLoss}
                  maxValuebps={100}
                  iconColorCard={
                    Array.isArray(packetLoss)
                      ? packetLoss[packetLoss?.length - 1]?.value > 80
                        ? "red"
                        : packetLoss[packetLoss?.length - 1]?.value <= 80 &&
                          packetLoss[packetLoss?.length - 1]?.value > 40
                          ? "orange"
                          : "green"
                      : "green"
                  }
                  handleAplicarZoom={handleAplicarZoom}
                  isZoomEnabled={true}
                />
              </Grid>

              <Grid item xs={2} sm={4} md={6} lg={6} xl={3}>
                <CardGraphComponent
                  titleGraph={"Latência"}
                  data={Array.isArray(latency) ? latency : []}
                  icon={SpeedOutlined}
                  valueCard={formatPing(
                    Array.isArray(latency)
                      ? latency?.[latency?.length - 1]?.value || 0
                      : 0,
                  )}
                  tickFormatter={formatPing}
                  loading={loadingDashOverview?.latency}
                  CustomTooltip={
                    <ChartTooltip title="Latência" formatValue={formatPing} />
                  }
                  iconColorCard={
                    Array.isArray(latency)
                      ? latency?.[latency?.length - 1]?.value > 100
                        ? "red"
                        : latency[latency?.length - 1]?.value <= 80 &&
                          latency[latency?.length - 1]?.value > 50
                          ? "orange"
                          : "green"
                      : "green"
                  }
                  handleAplicarZoom={handleAplicarZoom}
                  isZoomEnabled={true}
                />
              </Grid>
              {onu?.onu?.length > 0 && (
                <Grid item xs={2} sm={4} md={6} lg={6} xl={3}>
                  <CardGraphComponent
                    titleGraph={"Potência recebida"}
                    data={
                      Array.isArray(onuPower?.rx)
                        ? INVERSED_POWER_RX
                          ? onuPower?.rx?.map((item) => ({
                            ...item,
                            value: -item?.value,
                          }))
                          : onuPower?.rx
                        : []
                    }
                    icon={DownloadOutlined}
                    valueCard={`${Array.isArray(onuPower?.rx)
                      ? onuPower?.rx[
                        onuPower?.rx?.length - 1
                      ]?.value?.toFixed(2) || 0
                      : 0
                      } dBm`}
                    maxValuebps={INVERSED_POWER_RX ? 40 : 0}
                    minValuebps={INVERSED_POWER_RX ? 0 : -40}
                    tickFormatter={formatPowerValue}
                    loading={loadingPower.rx}
                    CustomTooltip={<CustomTooltipPower rx={true} />}
                    iconColorCard={
                      Array.isArray(onuPower?.rx)
                        ? onuPower?.rx[onuPower?.rx?.length - 1]?.value <= -27
                          ? "red"
                          : onuPower?.rx[onuPower?.rx?.length - 1]?.value <= -25
                            ? "yellow"
                            : "green"
                        : "green"
                    }
                    topRightItem={AutocompleteOnu}
                    handleAplicarZoom={handleAplicarZoom}
                    isZoomEnabled={true}
                  />
                </Grid>
              )}
              {onu?.onu?.length > 0 && (
                <Grid item xs={2} sm={4} md={6} lg={6} xl={3}>
                  <CardGraphComponent
                    titleGraph={"Potência transmitida"}
                    data={Array.isArray(onuPower?.tx) ? onuPower?.tx : []}
                    icon={PublishOutlined}
                    valueCard={`${Array.isArray(onuPower?.tx)
                      ? onuPower?.tx[
                        onuPower?.tx?.length - 1
                      ]?.value?.toFixed(2) || 0
                      : 0
                      } dBm`}
                    tickFormatter={(value) => `${value?.toFixed(0)} dBm`}
                    loading={loadingPower.tx}
                    CustomTooltip={CustomTooltipPower}
                    iconColorCard={
                      Array.isArray(onuPower?.tx)
                        ? onuPower?.tx[onuPower?.tx?.length - 1]?.value > 100
                          ? "red"
                          : onuPower?.tx[onuPower?.tx?.length - 1]?.value <=
                            80 &&
                            onuPower?.tx[onuPower?.tx?.length - 1]?.value > 50
                            ? "orange"
                            : "green"
                        : "green"
                    }
                    topRightItem={AutocompleteOnu}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item xs={2} sm={4} md={6} lg={6}>
            <Grid
              container
              columns={{ xs: 2, sm: 4, md: 6, lg: 6, xl: 6, xxl: 6 }}
            >
              <Grid item xs={2} sm={4} md={6} lg={6}>
                <AlertsTable
                  rows={rows}
                  loading={loadingDashOverview?.alerts}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      <PingDetailModal
        open={pingModalOpen}
        destino={pingModalDestino}
        data={pingHistoryData}
        loading={pingHistoryLoading}
        onClose={handlePingModalClose}
        onZoom={handlePingModalZoom}
        formatPing={formatPing}
        formatPacketLoss={formatPacketLoss}
      />
    </>
  );
}
